import DashboardHeader from "../dashboardheader/DashboardHeader";
import {
    DatePicker,
    Button,
    Row,
    Col,
    Card,
    Table,
    Select,
    Modal,
    Input,
    Descriptions,
} from "antd";
import {
    SearchOutlined,
    DownloadOutlined,
    ScheduleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Option } = Select;

export default function ViewEmpAttendance() {
    const navigate = useNavigate();
    const { authData, backUrl } = useContext(AuthContext);

    const [attendanceData, setAttendanceData] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    // Lead details modal state
    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter state
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);
    const [filterStatus, setFilterStatus] = useState("");

    // Modal state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [requestReason, setRequestReason] = useState("");
    const [attendanceId, setAttendanceId] = useState(0);

    // Build API URL with all query parameters
    const buildApiUrl = () => {
        const params = new URLSearchParams();

        params.append('page', currentPage);

        if (filterFromDate) {
            params.append('from_date', dayjs(filterFromDate).format('YYYY-MM-DD'));
        }
        if (filterToDate) {
            params.append('to_date', dayjs(filterToDate).format('YYYY-MM-DD'));
        }
        if (filterStatus) {
            params.append('status', filterStatus);
        }

        return `${backUrl}/api/hr/attendance/?${params.toString()}`;
    };

    // Fetch attendance data from backend
    const fetchData = async () => {
        setLoading(true);
        try {
            const url = buildApiUrl();
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });

            const formattedData = res.data.results.map((item) => ({
                ...item,
                Date: dayjs(item.Date).format("DD/MM/YYYY"),
                consideration_requests: item.consideration_requests || [],
                permissions: item.permissions || [],
            }));

            setAttendanceData(formattedData);
            setTotalRecords(res.data.count || 0);
        } catch (err) {
            console.error("Error fetching attendance data", err);
            alert("Failed to fetch attendance data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when pagination or filters change
    useEffect(() => {
        fetchData();
    }, [currentPage, pageSize, filterFromDate, filterToDate, filterStatus]);

    // Reset to page 1 when filters change
    const handleFilterChange = (filterSetter, value) => {
        filterSetter(value);
        setCurrentPage(1);
    };

    // Table columns definition
    const columns = [
        { title: "Employee Code", dataIndex: "employee_code", key: "employee_code" },
        { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
        { title: "Date", dataIndex: "date", key: "date" },
        {
            title: "Work Location",
            dataIndex: "work_location",
            key: "work_location",
            render: (site, record) =>
                site === "On-Duty" ? (
                    <>
                        <p style={{ marginBottom: 4 }}>{site}</p>
                        <p>{record.check_in_out_mode}</p>
                        {record?.onduty_type_check_in && (
                            <p style={{ marginBottom: 4, fontSize: "12px", color: "#666" }}>
                                {record.onduty_type_check_in}
                            </p>
                        )}
                        {
                            record?.lead_details &&
                            record.lead_details.length > 0 && (
                                <a
                                    onClick={() => {
                                        setSelectedLeadDetails(record.lead_details);
                                        setShowLead(true);
                                    }}
                                    style={{ color: "#1677ff", cursor: "pointer" }}
                                >
                                    Lead Details
                                </a>
                            )}
                    </>
                ) : (
                    <p>{site}</p>
                ),
        },
        { title: "Punch-In", dataIndex: "punch_in", key: "punch_in" },
        { title: "Check-In", dataIndex: "check_in", key: "check_in" },
        {
            title: "Check-In Photo",
            dataIndex: "checkInPhoto",
            key: "checkInPhoto",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Check-In Location",
            dataIndex: "check_in_location",
            key: "check_in_location",
            width:250,
            render: (text, record) => (
                <p style={{ margin: 0 }}>
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${record.check_in_latitude},${record.check_in_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff", textDecoration: "none", cursor: "pointer" }}
                    >
                        {text}
                    </a>
                </p>
            ),
        },
        {
            title: "Check-In Site",
            key: "check_in_site",
            render: (_, record) => (
                <>
                    {record.check_in_site || "-"}
                    {record.onduty_type_check_in && (
                        <> ({record.onduty_type_check_in})</>
                    )}
                </>
            ),
        },
        { title: "Punch-Out", dataIndex: "punch_out", key: "punch_out" },
        { title: "Check-Out", dataIndex: "check_out", key: "check_out" },
        {
            title: "Check-Out Photo",
            dataIndex: "checkOutPhoto",
            key: "checkOutPhoto",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-Out"
                        style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    "-"
                ),
        },

        {
            title: "Check-Out Location",
            dataIndex: "check_out_location",
            key: "check_out_location",
            width:250,
            render: (text, record) => (
                <a
                    href={`https://www.google.com/maps/search/?api=1&query=${record.check_out_latitude},${record.check_out_longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1677ff", textDecoration: "underline", cursor: "pointer" }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: "Check-Out Site",
            key: "check_out_site",
            render: (_, record) => (
                <>
                    {record.check_out_site || "-"}
                    <p>{record.check_in_out_mode}</p>

                    {record.onduty_type_check_out && (
                        <> ({record.onduty_type_check_out})</>
                    )}
                </>
            ),
        },
        { title: "First In", dataIndex: "first_in", key: "first_in" },
        { title: "Last Out", dataIndex: "last_out", key: "last_out" },
        {
            title: "Attendance Status",
            key: "status",
            dataIndex: "status",
            render: (status, record) => {
                const permissions = record.permissions || [];
                const considerationRequests = record.consideration_requests || [];

                // Find the Auto-Deduction permission specifically
                const autoPermission = permissions.find(
                    (p) => p.type === "Auto-Deduction"
                );

                // Only flag as auto-permission if status is PERMISSION AND an Auto-Deduction entry exists
                const isAutoPermission = status === "PERMISSION" && !!autoPermission;

                const statusColorMap = {
                    PRESENT: "#16a34a",
                    LATE: "#d97706",
                    LEAVE: "#dc2626",
                    PERMISSION: "#2563eb",
                };

                const statusColor = statusColorMap[status] || "#000";

                // Determine the main display label
                const displayStatus =
                    status === "LEAVE"
                        ? record.leavetype
                        : status === "CASUAL"
                            ? "Casual Leave"
                            : status === "SICK"
                                ? "Sick Leave"
                                : status === "SUNDAY"
                                    ? "Week Off"
                                    : status || "-";

                return (
                    <div style={{ textAlign: "center" }}>
                        {/* Main status badge */}
                        <div style={{ fontWeight: 600, color: statusColor }}>
                            {displayStatus}
                        </div>

                        {/* If status changed to PRESENT via a consideration request, show info */}
                        {status === "Present" && considerationRequests.length > 0 && (
                            <>
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Changed via consideration
                                </small>
                                <br />
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Remarks: {considerationRequests[0]?.remarks || "-"}
                                </small>
                                <br />
                            </>
                        )}

                        {/* ─── Auto-Deduction block ─── */}
                        {/* {isAutoPermission && (
                            <>
                                <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                    Auto-generated permission ({autoPermission.hours_requested} hr)
                                </small>
                                <br />

                                {considerationRequests.length === 0 ? (
                                    <Button
                                        onClick={() => {
                                            setAttendanceId(record.id);
                                            setIsModalVisible(true);
                                        }}
                                        className="mt-1"
                                        style={{
                                            backgroundColor: "#0e497b",
                                            color: "white",
                                            height: "28px",
                                            fontSize: "12px",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        Request
                                    </Button>
                                ) : (
                                    <>
                                        <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                            Request Status
                                        </small>
                                        <br />
                                        <div
                                            style={{
                                                fontWeight: 600,
                                                fontSize: "12px",
                                                color:
                                                    considerationRequests[0]?.status === "Approved"
                                                        ? "#16a34a"
                                                        : considerationRequests[0]?.status === "Rejected"
                                                            ? "#dc2626"
                                                            : "#d97706",
                                            }}
                                        >
                                            {considerationRequests[0]?.status || "-"}
                                        </div>
                                        {considerationRequests[0]?.remarks && (
                                            <small style={{ color: "#6b7280", fontSize: "11px" }}>
                                                Remarks: {considerationRequests[0].remarks}
                                            </small>
                                        )}
                                    </>
                                )}
                            </>
                        )} */}
                    </div>
                );
            },
        },
    ];

    // Transform raw API data → flat objects consumed by <Table>
    const dataSource = attendanceData.map((item) => ({
        key: `${item.EmployeeCode}_${item.Date}`,
        id: item.id,
        employee_code: item.EmployeeCode || "-",
        employee_name: item.EmployeeName || "-",
        date: item.Date || "-",
        work_location: item.WorkLocation || "-",
        check_in: item.CheckIn || "-",
        punch_in: item.PunchIn || "-",
        check_out: item.CheckOut || "-",
        punch_out: item.PunchOut || "-",
        first_in: item.FirstInType || "-",
        last_out: item.LastOutType || "-",
        status: item.AttendanceStatus || "-",
        checkInPhoto: item.check_in_photo_url,
        checkOutPhoto: item.check_out_photo_url,
        check_in_location: item.check_in_location || "-",
        check_out_location: item.check_out_location || "-",
        check_in_site: item.check_in_work_site || "-",
        check_out_site: item.check_out_work_site || "-",
        check_in_latitude: item.check_in_latitude,
        check_in_longitude: item.check_in_longitude,
        check_out_latitude: item.check_out_latitude,
        check_out_longitude: item.check_out_longitude,
        permissions: item.permissions || [],
        consideration_requests: item.consideration_requests || [],
        leavetype: item.Status || "-",
        onduty_type_check_in: item.onduty_type_check_in,
        onduty_type_check_out: item.onduty_type_check_out,
        // ✅ Pass lead_details through so the Work Location column can render the link
        lead_details: item.lead_details || [],
    }));

    // Row selection configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    // Download selected records as CSV
    const downloadSelected = () => {
        if (!selectedRowKeys.length) {
            alert("Please select at least one record to download.");
            return;
        }

        const selectedRows = dataSource.filter((row) =>
            selectedRowKeys.includes(row.key)
        );

        const headers = columns.map((col) => col.title).join(",");

        const rows = selectedRows.map((row) =>
            columns
                .map((col) => {
                    const value =
                        row[col.dataIndex] !== undefined
                            ? row[col.dataIndex]
                            : row[col.key] !== undefined
                                ? row[col.key]
                                : "";

                    return typeof value === "string"
                        ? `"${value.replace(/"/g, '""')}"`
                        : value || "";
                })
                .join(",")
        );

        const csvContent = [headers, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", "attendance_selected.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Handle table pagination changes
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // Clear all filters
    const clearFilters = () => {
        setFilterFromDate(null);
        setFilterToDate(null);
        setFilterStatus("");
        setCurrentPage(1);
    };

    return (
        <>
            {/* Lead Details Modal */}
            <Modal
                open={showLead}
                footer={null}
                onCancel={() => {
                    setShowLead(false);
                    setSelectedLeadDetails(null);
                }}
                width={700}
                title="Lead Details"
            >
                {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {selectedLeadDetails.map((lead, index) => (
                            <Descriptions
                                key={index}
                                bordered
                                column={1}
                                size="small"
                                style={{ marginBottom: index < selectedLeadDetails.length - 1 ? 16 : 0 }}
                            >
                                <Descriptions.Item label="Lead Number">
                                    <strong>{lead.lead_no || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {lead.customer_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {lead.email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {lead.number || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {lead.project_name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: lead.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#fff2e8"
                                                : "#f6ffed",
                                            color: lead.status
                                                ?.toLowerCase()
                                                .includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {lead.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        No lead details available
                    </div>
                )}
            </Modal>

            <center>
                <h5 style={{ textAlign: "center" }}>Attendance Records</h5>
            </center>

            <center>
                <section className="container mt-4" style={{ margin: "20px", alignItems: "center" }}>
                    <Card
                        style={{
                            padding: "20px",
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Row gutter={[16, 16]} justify="center" style={{ alignItems: "flex-end" }}>
                            <Col xs={24} sm={12} md={6} lg={6}>
                                <label>From Date</label>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={filterFromDate}
                                    onChange={(date) => handleFilterChange(setFilterFromDate, date)}
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={6}>
                                <label>To Date</label>
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={filterToDate}
                                    onChange={(date) => handleFilterChange(setFilterToDate, date)}
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={6}>
                                <label>Attendance Status</label>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder="Select Status"
                                    value={filterStatus}
                                    onChange={(value) => handleFilterChange(setFilterStatus, value)}
                                >
                                    <Option value="">None</Option>
                                    <Option value="PRESENT">Present</Option>
                                    <Option value="PERMISSION">Permission</Option>
                                    <Option value="HALFDAY_LEAVE">Halfday</Option>
                                    <Option value="Weekoff">Week Off</Option>
                                    <Option value="Compoff">Comp Off</Option>
                                    <Option value="Sick">Sick Leave</Option>
                                    <Option value="Casual">Casual Leave</Option>
                                    <Option value="ABSENT">Absent</Option>
                                    <Option value="HOLIDAY">Holiday</Option>
                                </Select>
                            </Col>

                            <Col xs={24} sm={12} md={6} lg={6} style={{ display: "flex", alignItems: "center" }}>
                                <Button
                                    style={{ width: "100%", marginTop: "24px" }}
                                    onClick={clearFilters}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    <Button
                        icon={<DownloadOutlined />}
                        block
                        style={{
                            height: "36px",
                            marginTop: "15px",
                            width: "180px",
                            backgroundColor: "#0e497b",
                            color: "white",
                        }}
                        onClick={downloadSelected}
                    >
                        Download Selected
                    </Button>

                    <Card
                        style={{
                            marginTop: "20px",
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            loading={loading}
                            bordered
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalRecords,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: "max-content" }}
                            rowSelection={rowSelection}
                            onChange={handleTableChange}
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th
                                            {...props}
                                            style={{
                                                backgroundColor: "#0e497b",
                                                color: "white",
                                                marginTop: "20px",
                                            }}
                                        >
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                        />
                    </Card>
                </section>
            </center>

            {/* Request Reason Modal */}
            <Modal
                title="Request Reason"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setRequestReason("");
                }}
                centered
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsModalVisible(false);
                            setRequestReason("");
                        }}
                        style={{
                            backgroundColor: "#f0f0f0",
                            color: "#333",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitting}
                        onClick={async () => {
                            try {
                                if (!requestReason.trim()) {
                                    alert("Reason for request is mandatory");
                                    return;
                                }

                                setSubmitting(true);

                                const payload = {
                                    attendance_id: attendanceId,
                                    remarks: requestReason,
                                };

                                const res = await axios.post(
                                    `${backUrl}/api/hr/attendance-requests/`,
                                    payload,
                                    {
                                        headers: {
                                            Authorization: `Bearer ${authData?.access}`,
                                            "Content-Type": "application/json",
                                        },
                                    }
                                );

                                alert(res.data?.message || "Request submitted successfully");
                                fetchData();
                                setIsModalVisible(false);
                                setRequestReason("");
                            } catch (error) {
                                console.error(error);
                                const errMsg =
                                    error.response?.data?.message ||
                                    error.response?.data?.detail ||
                                    "Failed to submit request";
                                alert(errMsg);
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            color: "#fff",
                            borderRadius: "6px",
                        }}
                    >
                        Submit
                    </Button>,
                ]}
            >
                <p>Please enter the reason for your request:</p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter your reason..."
                    value={requestReason}
                    onChange={(e) => setRequestReason(e.target.value)}
                />
            </Modal>
        </>
    );
}